// client/src/pages/BookingsPage.js

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Form, Modal } from 'react-bootstrap';
import { fetchUserBookings, cancelBooking, cancelPendingBooking, addConfirmationNumber } from '../services/api';
import { formatInTimeZone } from 'date-fns-tz';
import BookingCard from '../components/BookingCard';
import BookingCardSkeleton from '../components/BookingCardSkeleton';

const formatDateTime = (isoString) => {
  if (!isoString) return 'N/A';
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatInTimeZone(isoString, userTimeZone, 'MM/dd/yyyy, h:mm a');
};


const BookingsPage = () => {
  const [allBookings, setAllBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // ✅ 2. Add state for the new modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [bookingToUpdate, setBookingToUpdate] = useState(null);
  const [confirmationNumberInput, setConfirmationNumberInput] = useState("");

  // Cancel-booking modal state — cancellation runs a real Puppeteer flow against the
  // course's site (can take 15-30s), so this needs to visibly stay open and block a
  // second click, not just fire-and-forget like a plain window.confirm.
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelingBooking, setCancelingBooking] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);

  const loadBookings = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchUserBookings();
      setAllBookings(data.confirmed || []);
      setPendingBookings(data.pending || []);
    } catch (err) {
      setError('Failed to load bookings.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const handleCancelBookingClick = (booking) => {
    const hasConfirmations = !!booking.confirmation_numbers && booking.confirmation_numbers.length > 0;

    if (!hasConfirmations) {
      alert("Add your confirmation number(s) from the booking email before canceling — the course's website needs it to look up the reservation.");
      handleAddConfirmationClick(booking);
      return;
    }

    setCancelingBooking(booking);
    setShowCancelModal(true);
  };

  const handleConfirmCancelBooking = async () => {
    // Guards against a second click firing a second concurrent cancellation request —
    // the course's site only allows one active session, so two at once collide.
    if (!cancelingBooking || isCanceling) return;

    setIsCanceling(true);
    try {
      const result = await cancelBooking(cancelingBooking.id);
      if (result.success) {
        alert('Booking successfully canceled!');
        loadBookings();
      } else {
        setError(result.error || 'Failed to cancel booking.');
      }
    } catch (err) {
      setError(err.error || 'An unexpected error occurred.');
    } finally {
      setIsCanceling(false);
      setShowCancelModal(false);
      setCancelingBooking(null);
    }
  };

  const handleCancelPendingBooking = async (pendingBookingId) => {
    if (!window.confirm("Are you sure you want to cancel this pending request?")) {
      return;
    }
    try {
      const result = await cancelPendingBooking(pendingBookingId);
      if (result.success) {
        alert('Request successfully canceled!');
        loadBookings();
      } else {
        setError(result.error || 'Failed to cancel request.');
      }
    } catch (err) {
      setError(err.error || 'An unexpected error occurred.');
    }
  };

  const handleAddConfirmationClick = (booking) => {
    setBookingToUpdate(booking);
    if (booking.confirmation_numbers && booking.confirmation_numbers.length > 0) {
      setConfirmationNumberInput(booking.confirmation_numbers.join(','));
    } else {
      setConfirmationNumberInput('');
    } setShowConfirmationModal(true);
  };

  const handleSaveConfirmation = async () => {
    if (!bookingToUpdate || !confirmationNumberInput.trim()) {
      alert('Please enter a valid confirmation number.');
      return;
    }

    try {
      const result = await addConfirmationNumber(bookingToUpdate.id, confirmationNumberInput);

      if (result.success) {
        setShowConfirmationModal(false);
        loadBookings(); // Refresh the bookings list to show the new number
      } else {
        setError(result.error || 'Failed to save confirmation number.');
      }
    } catch (err) {
      setError(err.error || 'An unexpected error occurred.');
    }
  };

  const handleEditClick = (booking) => {
    alert(`Editing for ${booking.course_name} is coming soon!`);
  };

  const now = new Date();
  const upcomingBookings = allBookings.filter(b => new Date(b.start_time) >= now);
  const pastBookings = allBookings.filter(b => new Date(b.start_time) < now);

  if (isLoading) {
    return (
      <Container className="mt-4">
        <h2>Your Bookings</h2>
        <h4 className="mt-4 text-muted">Pending Requests</h4>
        <Row>
          <BookingCardSkeleton />
          <BookingCardSkeleton />
        </Row>
        <h4 className="mt-4 text-muted">Upcoming Tee Times</h4>
        <Row>
          <BookingCardSkeleton />
        </Row>
      </Container>
    );
  }

  return (
    <>
      <Container className="mt-4">
        <h2>Your Bookings</h2>
        {error && <Alert variant="danger">{error}</Alert>}

        {/* ✅ UPDATED: Pending Bookings Section now uses BookingCard */}
        <h4 className="mt-4">Pending</h4>
        {pendingBookings.length > 0 ? (
          <Row>
            {pendingBookings.map((booking) => (
              <Col md={6} key={`pend-${booking.id}`}>
                <BookingCard
                  booking={booking}
                  onCancel={handleCancelPendingBooking}
                  onEdit={handleEditClick}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <p>You have no pending scheduled or standby bookings.</p>
        )}

        {/* Upcoming Confirmed Bookings (no changes needed) */}
        <h4 className="mt-4">Upcoming</h4>
        {upcomingBookings.length > 0 ? (
          <Row>
            {upcomingBookings.map((booking) => (
              <Col md={6} key={`conf-${booking.id}`}>
                <BookingCard
                  booking={booking}
                  onCancel={handleCancelBookingClick}
                  onAddConfirmation={handleAddConfirmationClick}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <p>You have no upcoming bookings.</p>
        )}

        {/* Past Bookings Section */}
        <div className="d-flex justify-content-between align-items-center mt-5">
          <h4>Booking History</h4>
          <Button variant="link" onClick={() => setShowHistory(!showHistory)}>
            {showHistory ? 'Hide History' : 'Show History'}
          </Button>
        </div>
        <hr />
        {showHistory && (
          pastBookings.length > 0 ? (
            <Row>
              {pastBookings.map((booking) => (
                <Col md={6} key={`past-${booking.id}`}>
                  <BookingCard booking={booking} onCancel={handleCancelBookingClick} />
                </Col>
              ))}
            </Row>
          ) : (
            <p>You have no past bookings.</p>
          )
        )}
      </Container>

      {/* ✅ 6. Add the Modal component */}
      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)} centered>
        <Modal.Header closeButton>
            {bookingToUpdate?.confirmation_numbers?.length > 0 ? 'Edit' : 'Add'} Confirmation Numbers
        </Modal.Header>
        <Modal.Body>
          <p>For <strong>{bookingToUpdate?.course_name}</strong> on {bookingToUpdate && new Date(bookingToUpdate.start_time).toLocaleDateString()}.</p>
          <Form.Group>
            <Form.Label>Confirmation Number</Form.Label>
            <Form.Control
              type="text"
              value={confirmationNumberInput}
              onChange={(e) => setConfirmationNumberInput(e.target.value)}
              placeholder="Enter number from your email"
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>Close</Button>
          <Button variant="primary" onClick={handleSaveConfirmation}>Save Confirmation</Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Booking modal — stays open and disables both buttons while the cancellation
          is actually running against the course's site, so a second click can't fire a
          second concurrent request for the same booking. */}
      <Modal
        show={showCancelModal}
        onHide={() => !isCanceling && setShowCancelModal(false)}
        backdrop={isCanceling ? 'static' : true}
        keyboard={!isCanceling}
        centered
      >
        <Modal.Header closeButton={!isCanceling}>
          Cancel Booking
        </Modal.Header>
        <Modal.Body>
          {isCanceling ? (
            <div className="text-center py-3">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="mb-0">
                Canceling on the course's website — this can take 15-30 seconds.
                Please don't close this or click cancel again.
              </p>
            </div>
          ) : (
            <p className="mb-0">
              Are you sure you want to cancel your booking at <strong>{cancelingBooking?.course_name}</strong>?
              This will cancel it on the course's website too.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)} disabled={isCanceling}>
            Close
          </Button>
          <Button variant="danger" onClick={handleConfirmCancelBooking} disabled={isCanceling}>
            {isCanceling ? 'Canceling...' : 'Yes, Cancel'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BookingsPage;